import { FFmpeg } from '@ffmpeg/ffmpeg';
// On deployed envs the JS bundle is served from the CDN, so ffmpeg's default
// `new Worker(new URL('./worker.js', import.meta.url))` resolves cross-origin and the browser
// blocks it. Load the worker chunk through a same-origin blob URL instead (same trick as core/wasm).
import ffmpegClassWorkerUrl from '@ffmpeg/ffmpeg/worker?worker&url';
import { toBlobURL, fetchFile } from '@ffmpeg/util';
import { fetchMasterPlaylist, fetchMediaPlaylist, fetchSegmentRange } from './hlsPlaylist';

// Single-threaded core: no COOP/COEP headers needed.
const FFMPEG_CORE_BASE_URL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm';

// Matched by resolution, not BANDWIDTH (encoder-specific, matched the wrong variant).
const TARGET_VARIANT_HEIGHT = 480;

// Ratio not px, so the logo scales with the target resolution.
const LOGO_HEIGHT_RATIO = 35 / 576;
const LOGO_MARGIN_PX = 16;
const MUSIC_VOLUME = 0.02;

let ffmpegPromise = null;
const getFfmpeg = async () => {
  if (!ffmpegPromise) {
    ffmpegPromise = (async () => {
      const ffmpeg = new FFmpeg();
      await ffmpeg.load({
        classWorkerURL: await toBlobURL(ffmpegClassWorkerUrl, 'text/javascript'),
        coreURL: await toBlobURL(`${FFMPEG_CORE_BASE_URL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${FFMPEG_CORE_BASE_URL}/ffmpeg-core.wasm`, 'application/wasm'),
      });
      return ffmpeg;
    })();
  }
  return ffmpegPromise;
};

// ffmpeg has no SVG decoder — rasterize once, well above overlay size, so it downscales crisp.
const LOGO_RASTER_HEIGHT_PX = 200;
const rasterizeSvgToPng = async (svgUrl) => {
  const svgText = await (await fetch(svgUrl)).text();
  const blobUrl = URL.createObjectURL(new Blob([svgText], { type: 'image/svg+xml' }));
  try {
    const image = new Image();
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = () => reject(new Error('Failed to rasterize logo SVG.'));
      image.src = blobUrl;
    });
    const targetHeight = LOGO_RASTER_HEIGHT_PX;
    const aspectRatio = image.naturalWidth / image.naturalHeight || 1;
    const canvas = document.createElement('canvas');
    canvas.height = targetHeight;
    canvas.width = Math.round(targetHeight * aspectRatio);
    canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
    const pngBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    return new Uint8Array(await pngBlob.arrayBuffer());
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
};

// Cuts [startSeconds, endSeconds] from the HLS source, overlays the logo, mixes music, returns
// an mp4 Blob. onProgress { message, percent, remainingSeconds }: phases below are checkpoints,
// encoding 35-100% tracks ffmpeg's real ratio.
const PHASE_PERCENT = { fetching: 0, loadingEngine: 20, encoding: 35 };

const generateClip = async ({
  masterPlaylistUrl,
  startSeconds,
  endSeconds,
  logoUrl,
  musicTrack,
  targetHeight = TARGET_VARIANT_HEIGHT,
  onProgress,
}) => {
  onProgress?.({ message: 'Fetching clip footage…', percent: PHASE_PERCENT.fetching });
  const master = await fetchMasterPlaylist(masterPlaylistUrl);
  if (!master.videoVariants.length) throw new Error('No video renditions found in playlist.');
  const byHeight = [...master.videoVariants].sort((a, b) => (a.height ?? a.bandwidth) - (b.height ?? b.bandwidth));
  const variant = byHeight.find((item) => (item.height ?? 0) >= targetHeight) ?? byHeight[byHeight.length - 1];

  const [videoPlaylist, audioPlaylist] = await Promise.all([
    fetchMediaPlaylist(variant.url),
    master.audioUrl ? fetchMediaPlaylist(master.audioUrl) : Promise.resolve(null),
  ]);

  const [videoRange, audioRange, musicBytes, logoBytes] = await Promise.all([
    fetchSegmentRange({ mediaPlaylist: videoPlaylist, startSeconds, endSeconds }),
    audioPlaylist
      ? fetchSegmentRange({ mediaPlaylist: audioPlaylist, startSeconds, endSeconds })
      : Promise.resolve(null),
    musicTrack ? fetchFile(musicTrack.url) : Promise.resolve(null),
    rasterizeSvgToPng(logoUrl),
  ]);

  onProgress?.({ message: 'Loading video engine…', percent: PHASE_PERCENT.loadingEngine });
  const ffmpeg = await getFfmpeg();

  onProgress?.({ message: 'Trimming, overlaying logo and mixing audio…', percent: PHASE_PERCENT.encoding });
  await ffmpeg.writeFile('video.mp4', videoRange.bytes);
  if (audioRange) await ffmpeg.writeFile('audio.mp4', audioRange.bytes);
  if (musicBytes) await ffmpeg.writeFile('music.mp3', musicBytes);
  await ffmpeg.writeFile('logo-overlay.png', logoBytes);

  const audioIndex = audioRange ? 1 : null;
  const musicIndex = musicBytes ? (audioRange ? 2 : 1) : null;
  const logoIndex = 1 + (audioRange ? 1 : 0) + (musicBytes ? 1 : 0);

  const trimStart = Math.max(0, startSeconds - videoRange.rangeStart);
  const clipDuration = Math.max(0, endSeconds - startSeconds);
  // Scale down to the exact target: `variant` is the smallest rendition >= targetHeight and is
  // often bigger, and the encode cost (single-threaded WASM x264) scales with pixels.
  const logoHeightPx = Math.round(targetHeight * LOGO_HEIGHT_RATIO);

  const filters = [
    `[0:v]scale=-2:${targetHeight}[scaled]`,
    `[${logoIndex}:v]scale=-1:${logoHeightPx}[logo]`,
    `[scaled][logo]overlay=${LOGO_MARGIN_PX}:${LOGO_MARGIN_PX}[v]`,
  ];
  let audioMapArgs = [];
  if (audioRange && musicBytes) {
    filters.push(`[${musicIndex}:a]volume=${MUSIC_VOLUME}[bg]`);
    filters.push(`[${audioIndex}:a][bg]amix=inputs=2:duration=first:dropout_transition=0[a]`);
    audioMapArgs = ['-map', '[a]'];
  } else if (audioRange) {
    audioMapArgs = ['-map', `${audioIndex}:a`];
  } else if (musicBytes) {
    filters.push(`[${musicIndex}:a]volume=${MUSIC_VOLUME}[a]`);
    audioMapArgs = ['-map', '[a]'];
  }
  const hasAudioOutput = audioMapArgs.length > 0;

  const args = [
    '-i',
    'video.mp4',
    ...(audioRange ? ['-i', 'audio.mp4'] : []),
    ...(musicBytes ? ['-stream_loop', '-1', '-i', 'music.mp3'] : []),
    '-i',
    'logo-overlay.png',
    '-filter_complex',
    filters.join(';'),
    '-map',
    '[v]',
    ...audioMapArgs,
    '-ss',
    `${trimStart}`,
    '-t',
    `${clipDuration}`,
    '-c:v',
    'libx264',
    // veryfast+23 over fast+18: bigger file, but much faster on single-threaded WASM x264 — the
    // right trade for a quick social clip.
    '-preset',
    'veryfast',
    '-crf',
    '23',
    ...(hasAudioOutput ? ['-c:a', 'aac'] : []),
    ...(musicTrack ? ['-metadata', `comment=${musicTrack.credit}`] : []),
    '-movflags',
    '+faststart',
    'output.mp4',
  ];
  // Unbound after use — the ffmpeg instance is memoized and reused across generations.
  const encodeStartedAt = Date.now();
  const handleProgress = ({ progress: ratio }) => {
    const clampedRatio = Math.max(0, Math.min(1, ratio));
    const percent = Math.round(PHASE_PERCENT.encoding + clampedRatio * (100 - PHASE_PERCENT.encoding));
    const elapsedMs = Date.now() - encodeStartedAt;
    const remainingSeconds =
      clampedRatio > 0.03 ? Math.round(((elapsedMs / clampedRatio) * (1 - clampedRatio)) / 1000) : null;
    onProgress?.({ message: 'Encoding clip…', percent, remainingSeconds });
  };
  ffmpeg.on('progress', handleProgress);
  try {
    await ffmpeg.exec(args);
  } finally {
    ffmpeg.off('progress', handleProgress);
  }

  const data = await ffmpeg.readFile('output.mp4');
  await Promise.all(
    ['video.mp4', 'audio.mp4', 'music.mp3', 'logo-overlay.png', 'output.mp4']
      .filter((name) => (name !== 'audio.mp4' || audioRange) && (name !== 'music.mp3' || musicBytes))
      .map((name) => ffmpeg.deleteFile(name).catch(() => {}))
  );

  return new Blob([data.buffer], { type: 'video/mp4' });
};

export { generateClip, TARGET_VARIANT_HEIGHT, MUSIC_VOLUME };
